import { useEffect, useRef } from 'react';
import { getUserprint } from '@narvar/userprint';
import { getAppsGatewayUrl } from '../../../../../../../constants/env';
import { willSkipAnalytics } from '../../willSkipAnalytics';

const WORKFLOW_ID = 'thanks.events';
const APP_NAME = 'thanks-ads';
const APP_VERSION = '1.0.0';

interface UseThanksAnalyticsParams {
  retailerMoniker: string;
  sessionId: string;
  device: 'mobile' | 'desktop';
  pageUrl: string;
  versionName: string;
  locale: string;
  orderNumber?: string;
  trackingNumber?: string;
  trackingStatus?: string;
  layout?: 'INLINE' | 'COVER';
  decisionId?: string;
  isFallback?: boolean;
}

export function useThanksAnalytics(params: UseThanksAnalyticsParams | null) {
  const hasFired = useRef(false);
  const pageStartTime = useRef(performance.now());

  useEffect(() => {
    if (!params || hasFired.current) return;

    if (willSkipAnalytics(params.pageUrl)) return;

    hasFired.current = true;

    (async () => {
      try {
        const { browser_fingerprint: browserFingerprint } =
          await getUserprint();

        const event = {
          eventName: 'widget_load' as const,
          sessionId: params.sessionId,
          device: params.device,
          appName: APP_NAME,
          appVersion: APP_VERSION,
          pageUrl: params.pageUrl,
          versionName: params.versionName,
          locale: params.locale,
          browserFingerprint,
          eventOffsetMs: Math.round(performance.now() - pageStartTime.current),
          ...(params.layout && { layout: params.layout }),
          ...(params.orderNumber && { orderNumber: params.orderNumber }),
          ...(params.trackingNumber && {
            trackingNumber: params.trackingNumber,
          }),
          ...(params.trackingStatus && {
            trackingStatus: params.trackingStatus,
          }),
          ...(params.decisionId && { decisionId: params.decisionId }),
          ...(params.isFallback && { isFallback: true }),
        };

        const appsGatewayUrl = getAppsGatewayUrl();

        await fetch(`${appsGatewayUrl}/invoke`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workflowId: WORKFLOW_ID,
            context: { retailerMoniker: params.retailerMoniker },
            body: event,
          }),
        });
      } catch (err) {
        console.error('Thanks event failed', err);
      }
    })();
  }, [params]);
}
